import { useId } from 'react';

// Asset imports - bundler handles path resolution
import mobileAppleStoreIcon from '~/static/assets/images/landing-page/mobile_apple_store.png';
import mobileGoogleIcon from '~/static/assets/images/landing-page/mobile_google.png';
/*import mobileMacIcon from '~/static/assets/images/landing-page/mobile_mac.png';*/
import { MOBILE_APP_STORE_URLS } from '../../constants/mobileApps';

interface AppDownload {
  platform: 'ios' | 'android' | 'mac' | 'windows';
  downloadUrl: string;
  iconPath: ImageAsset;
  label: string;
  altText: string;
}

interface DownloadAppsSectionProps {
  mobileApps?: AppDownload[];
  /*desktopApps?: AppDownload[];*/
  sectionTitle?: string;
  description?: string;
}

const defaultMobileApps: AppDownload[] = [
  {
    platform: 'ios',
    downloadUrl: MOBILE_APP_STORE_URLS.ios,
    iconPath: mobileAppleStoreIcon,
    label: 'Download on the App Store',
    altText: 'Download TeamHub on the App Store',
  },
  {
    platform: 'android',
    downloadUrl: MOBILE_APP_STORE_URLS.android,
    iconPath: mobileGoogleIcon,
    label: 'Get it on Google Play',
    altText: 'Get TeamHub on Google Play',
  },
];

/* Desktop apps - HIDDEN FOR NOW - https://sangoma.atlassian.net/browse/SM-1201
const defaultDesktopApps: AppDownload[] = [
  {
    platform: 'mac',
    downloadUrl: 'https://meet.sangoma.com/apps/desktop/latest/macos/',
    iconPath: mobileMacIcon,
    label: 'Download for Mac',
    altText: 'Download Sangoma Meet for Mac',
  },
];*/

export const DownloadAppsSection = ({
  mobileApps = defaultMobileApps,
  /*desktopApps = defaultDesktopApps,*/
  sectionTitle = 'Download apps for free',
  description = 'Video conference from anywhere with AI powered calls across all your digital devices.',
}: DownloadAppsSectionProps) => {
  const baseId = useId();
  const downloadTitleId = `${baseId}-download-section-title`;
  const mobileAppsTitleId = `${baseId}-mobile-apps-title`;
  /*const desktopAppsTitleId = `${baseId}-desktop-apps-title`;*/

  const handleDownloadClick = (app: AppDownload) => {
    // Open download URL
    if (app.downloadUrl !== '#') {
      window.open(app.downloadUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const renderAppButton = (app: AppDownload, type: 'mobile' | 'desktop') => {
    const downloadDescId = `${baseId}-${app.platform}-download-desc`;

    return (
      <button
        key={app.platform}
        type="button"
        className={`download-app-button ${type}-app-button ${app.platform}-button`}
        onClick={() => handleDownloadClick(app)}
        aria-label={app.altText}
        aria-describedby={downloadDescId}
      >
        <img
          src={app.iconPath.src}
          width={app.iconPath.width}
          height={app.iconPath.height}
          alt=""
          className="app-icon store-badge"
          loading="eager"
          decoding="async"
          aria-hidden={true}
        />
        <span className="sr-only">{app.label}</span>
      </button>
    );
  };

  return (
    <section className="download-apps-section" aria-labelledby={downloadTitleId}>
      <div className="download-apps-container">
        <div className="download-apps-background">
          <div className="download-apps-content">
            <div className="download-apps-left">
              <div className="download-section-label">USE IT EVERYWHERE</div>
              <h2 id={downloadTitleId} className="download-section-title">
                {sectionTitle}
              </h2>
              <p className="download-section-description">{description}</p>
            </div>

            <div className="download-apps-right">
              <div className="apps-row">
                <fieldset className="mobile-apps-group">
                  <legend id={mobileAppsTitleId} className="app-group-title">
                    Mobile apps
                  </legend>
                  <div className="mobile-apps-buttons">
                    {mobileApps.map((app) => (
                      <div key={app.platform}>
                        {renderAppButton(app, 'mobile')}
                        <div id={`${baseId}-${app.platform}-download-desc`} className="sr-only">
                          Download TeamHub for{' '}
                          {app.platform === 'ios' ? 'iPhone and iPad' : 'Android devices'}
                        </div>
                      </div>
                    ))}
                  </div>
                </fieldset>

                {/* Desktop apps - HIDDEN FOR NOW - https://sangoma.atlassian.net/browse/SM-1201
                <fieldset className="desktop-apps-group">
                  <legend id={desktopAppsTitleId} className="app-group-title">
                    Desktop app
                  </legend>
                  <div className="desktop-apps-buttons">
                    {desktopApps.map((app) => (
                      <div key={app.platform}>
                        {renderAppButton(app, 'desktop')}
                        <div id={`${baseId}-${app.platform}-download-desc`} className="sr-only">
                          Download Sangoma Meet for{' '}
                          {app.platform === 'mac' ? 'Mac computers' : 'Windows computers'}
                        </div>
                      </div>
                    ))}
                  </div>
                </fieldset>*/}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
